import { Link } from 'react-router';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Badge } from '@/components/ui/badge';
import { useCanOpen } from '@/hooks/useCanOpen';
import { formatDate } from '@/utils/dateUtils';
import { subsidyExpiryDocumentLabel } from '@/types/subsidyExpiry';

interface SubsidyExpiryPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: MessageDescriptor | { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

/** Local copy of WorkflowPayloadDetail's FieldRow (a detail panel, not a table). */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Renders a `subsidy_expiry` reminder (#2004): a Beitragsfaktor or SBU
 * confirmation expires in 90, 60 or 30 days, or has expired. A notice: the
 * office renews it on the contract (or from the dashboard widget) and
 * acknowledges the reminder; a renewal also clears it on the next sweep.
 */
export function SubsidyExpiryPayload({ payload, i18n }: SubsidyExpiryPayloadProps) {
  const canOpen = useCanOpen();
  const childName = readString(payload, 'child_name');
  const contractId = readString(payload, 'contract_id');
  const validUntil = readString(payload, 'valid_until');
  const document = readString(payload, 'document');
  const threshold = typeof payload.threshold === 'number' ? payload.threshold : undefined;
  const note = readString(payload, 'note');
  const expired = threshold === 0;

  return (
    <div className="space-y-4" data-testid="subsidy-expiry-payload">
      {childName && (
        <FieldRow label={i18n._(msg({ id: 'workflow.payload.childName', message: 'Child' }))}>
          <p className="text-sm">{childName}</p>
        </FieldRow>
      )}
      <FieldRow label={i18n._(msg({ id: 'subsidyExpiry.payload.document', message: 'Document' }))}>
        <p className="text-sm">{subsidyExpiryDocumentLabel(i18n, document)}</p>
      </FieldRow>
      {validUntil && (
        <FieldRow label={i18n._(msg({ id: 'subsidyExpiry.payload.validUntil', message: 'Valid until' }))}>
          <p className="flex items-center gap-2 text-sm">
            {formatDate(validUntil)}
            {/* Text-bearing badges, so the state is never colour-only. */}
            {expired ? (
              <Badge variant="destructive">{i18n._(msg({ id: 'subsidyExpiry.badge.expired', message: 'Expired' }))}</Badge>
            ) : threshold !== undefined ? (
              <Badge variant="outline">
                {i18n._({ id: 'subsidyExpiry.payload.threshold', message: 'Reminder {days} days ahead', values: { days: threshold } })}
              </Badge>
            ) : null}
          </p>
        </FieldRow>
      )}
      {document === 'sbu' && threshold === 60 && (
        <p className="text-sm text-muted-foreground">
          {i18n._(
            msg({
              id: 'subsidyExpiry.payload.sbuCityLetter',
              message: 'The city has written to the parents about the SBU renewal.',
            }),
          )}
        </p>
      )}
      {note && (
        <FieldRow label={i18n._(msg({ id: 'subsidyExpiry.payload.note', message: 'Note' }))}>
          <p className="text-sm whitespace-pre-wrap">{note}</p>
        </FieldRow>
      )}
      {contractId && canOpen(`/contracts/${contractId}/edit`) && (
        <Link to={`/contracts/${contractId}/edit`} className="text-sm text-primary underline underline-offset-4">
          {i18n._(msg({ id: 'subsidyExpiry.payload.openContract', message: 'Open the contract to enter the renewal' }))}
        </Link>
      )}
    </div>
  );
}
